/**
 * Delete Confirmation Dialog Component
 *
 * Simple confirmation dialog for deleting a student.
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material';
import { useDeleteStudentMutation } from '../../../api/studentsApi';
import type { Student } from '../../../types/students';

interface DeleteConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  studioId: string;
  student: Student | null;
}

const DeleteConfirmationDialog: React.FC<DeleteConfirmationDialogProps> = ({
  open,
  onClose,
  studioId,
  student,
}) => {
  const [deleteStudent, { isLoading }] = useDeleteStudentMutation();
  const [error, setError] = useState<string | null>(null);

  const handleDelete = async () => {
    if (!student) return;

    try {
      await deleteStudent({ studioId, studentId: student.id }).unwrap();
      onClose();
    } catch (err: any) {
      setError(
        err?.data?.message || 'Failed to delete student. Please try again.'
      );
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Delete Student</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Typography>
          Are you sure you want to delete <strong>{student?.full_name}</strong>?
          This action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          variant="contained"
          color="error"
          disabled={isLoading}
        >
          {isLoading ? <CircularProgress size={24} /> : 'Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DeleteConfirmationDialog;
